import { useState } from "react";
import { router, useForm } from "@inertiajs/react";
import {
  PlusIcon,
  TrashIcon,
  BoltIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { Copyable } from "@components/ui/copyable";

export interface GenericWebhook {
  id: number;
  name: string | null;
  target_url: string;
  target_host: string;
  last_delivered_at: string | null;
  created_at: string;
}

export interface RevealedWebhookSecret {
  id: number;
  name: string | null;
  secret: string;
}

export interface TestResult {
  webhook_id: number;
  status: number;
  latency_ms?: number;
  error?: string;
  ok: boolean;
}

interface GenericWebhooksPanelProps {
  webhooks: GenericWebhook[];
  revealedSecret: RevealedWebhookSecret | null;
  testResult: TestResult | null;
}

function formatRelative(iso: string | null) {
  if (!iso) return "Never";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function GenericWebhooksPanel({ webhooks, revealedSecret, testResult }: GenericWebhooksPanelProps) {
  const [showNewForm, setShowNewForm] = useState(webhooks.length === 0);

  const { data, setData, post, processing, reset } = useForm({
    webhook: { name: "", target_url: "" },
  });

  const handleCreate = () => {
    post("/integrations/webhooks", {
      preserveScroll: true,
      onSuccess: () => {
        setShowNewForm(false);
        reset();
      },
    });
  };

  const handleDelete = (id: number) => {
    if (!confirm("Delete this webhook? No more events will be sent to this URL.")) return;
    router.delete(`/integrations/webhooks/${id}`, { preserveScroll: true });
  };

  const handleTest = (id: number) => {
    router.post(`/integrations/webhooks/${id}/test`, {}, { preserveScroll: true });
  };

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-[#E5E5E4] bg-white p-5">
        <div className="flex items-start gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EEF0FB]">
            <BoltIcon className="h-5 w-5 text-[#5867C4]" />
          </div>
          <div className="flex-1">
            <h3 className="font-['Plus_Jakarta_Sans'] text-base font-semibold text-[#2E3238]">
              Send leads to any HTTPS endpoint
            </h3>
            <p className="mt-1 font-['Plus_Jakarta_Sans'] text-sm text-[#74767A]">
              We'll POST a signed JSON payload to your URL on every new lead. Verify the{" "}
              <code className="rounded bg-[#F5F5F4] px-1 py-0.5 text-xs">X-Launch10-Signature</code>{" "}
              header with HMAC-SHA256 using the secret we give you.
            </p>
          </div>
        </div>
      </section>

      {revealedSecret && (
        <section className="rounded-2xl border border-[#BDE8D2] bg-[#F0FAF5] p-5">
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-[#2E9E72]" />
            <p className="font-['Plus_Jakarta_Sans'] text-sm font-semibold text-[#2E3238]">
              Webhook created — copy your signing secret now
            </p>
          </div>
          <p className="mt-1 font-['Plus_Jakarta_Sans'] text-xs text-[#74767A]">
            You won't be able to see this secret again after you leave this page.
          </p>
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-[#D3D2D0] bg-white px-3 py-2">
            <code className="flex-1 truncate font-mono text-xs text-[#2E3238]">
              {revealedSecret.secret}
            </code>
            <Copyable text={revealedSecret.secret}>
              <Copyable.Trigger />
            </Copyable>
          </div>
        </section>
      )}

      <section className="rounded-2xl border border-[#E5E5E4] bg-white p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-['Plus_Jakarta_Sans'] text-base font-semibold text-[#2E3238]">
            Your webhooks
          </h3>
          {!showNewForm && (
            <Button size="sm" variant="outline" onClick={() => setShowNewForm(true)}>
              <PlusIcon className="h-4 w-4" />
              Add webhook
            </Button>
          )}
        </div>

        {showNewForm && (
          <div className="mt-4 space-y-3 rounded-xl border border-[#E5E5E4] bg-[#FAFAF9] p-4">
            <div className="space-y-2">
              <label className="font-['Plus_Jakarta_Sans'] text-sm font-semibold text-[#2E3238]">
                Name
              </label>
              <Input
                type="text"
                value={data.webhook.name}
                onChange={(e) => setData("webhook", { ...data.webhook, name: e.target.value })}
                placeholder="e.g. Production CRM"
                className="h-10 bg-white"
              />
            </div>
            <div className="space-y-2">
              <label className="font-['Plus_Jakarta_Sans'] text-sm font-semibold text-[#2E3238]">
                Endpoint URL
              </label>
              <Input
                type="url"
                value={data.webhook.target_url}
                onChange={(e) => setData("webhook", { ...data.webhook, target_url: e.target.value })}
                placeholder="https://your-server.com/webhooks/leads"
                className="h-10 bg-white"
              />
              <p className="font-['Plus_Jakarta_Sans'] text-xs text-[#96989B]">
                Must be HTTPS. Private and loopback addresses are not allowed.
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleCreate} disabled={processing}>
                {processing ? "Creating…" : "Create webhook"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setShowNewForm(false);
                  reset();
                }}
                disabled={processing}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {webhooks.length === 0 && !showNewForm && (
          <p className="mt-4 font-['Plus_Jakarta_Sans'] text-sm text-[#96989B]">
            No webhooks yet. Click "Add webhook" to create one.
          </p>
        )}

        {webhooks.length > 0 && (
          <div className="mt-4 space-y-2">
            {webhooks.map((wh) => {
              const result = testResult?.webhook_id === wh.id ? testResult : null;
              return (
                <div
                  key={wh.id}
                  className="rounded-lg border border-[#E5E5E4] bg-[#FAFAF9] px-4 py-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-['Plus_Jakarta_Sans'] text-sm font-medium text-[#2E3238]">
                        {wh.name || wh.target_host}
                      </p>
                      <p className="truncate font-['Plus_Jakarta_Sans'] text-xs text-[#96989B]">
                        {wh.target_url}
                      </p>
                      <p className="mt-0.5 font-['Plus_Jakarta_Sans'] text-xs text-[#96989B]">
                        Last delivery {formatRelative(wh.last_delivered_at)}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleTest(wh.id)}
                      >
                        Send test
                      </Button>
                      <button
                        type="button"
                        onClick={() => handleDelete(wh.id)}
                        className="text-[#96989B] hover:text-[#D14F34]"
                        aria-label="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  {result && (
                    <div
                      className={`mt-3 flex items-start gap-2 rounded-md px-3 py-2 text-xs ${
                        result.ok
                          ? "bg-[#F0FAF5] text-[#2E9E72]"
                          : "bg-[#FEF2EF] text-[#D14F34]"
                      }`}
                    >
                      {result.ok ? (
                        <CheckCircleIcon className="h-4 w-4 shrink-0" />
                      ) : (
                        <ExclamationCircleIcon className="h-4 w-4 shrink-0" />
                      )}
                      <span className="font-['Plus_Jakarta_Sans']">
                        {result.ok
                          ? `Delivered · ${result.status} · ${result.latency_ms}ms`
                          : `Failed · ${result.error || `HTTP ${result.status}`}`}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
